import { Button, Link, Text } from "@zapier/design-system";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  createNewTableAtom_SUSPENDS,
  shouldCreateNewTableAtom,
  updateNodeSetter,
  tablesUsageAtom_SUSPENDS,
  tablesAtom_SUSPENDS,
  getUpgradePlanType,
  emitEventOnTablesPlanUpgradeSetter,
  saveCanvasSetter,
} from "@zapier/canvas-state";
import { useCallback, useEffect } from "react";
import { useEmitResourceCreatedEvents } from "../../hooks/useEmitResourceCreatedEvents";
import { CREATE_NEW_TABLE_BUTTON_TEXT } from "@zapier/canvas-constants";

import useIsOwnerAdminOrSuperAdmin from "../../hooks/useIsOwnerAdminOrSuperAdmin";

export default function CreateNewTableButton({ nodeId }: { nodeId: string }) {
  const setShouldCreateNewTable = useSetAtom(shouldCreateNewTableAtom);
  const createNewTableResponse = useAtomValue(createNewTableAtom_SUSPENDS);
  const updateNode = useSetAtom(updateNodeSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const [tablesUsage, refreshTablesUsage] = useAtom(tablesUsageAtom_SUSPENDS);
  const refreshTables = useSetAtom(tablesAtom_SUSPENDS);
  const isAdmin = useIsOwnerAdminOrSuperAdmin();
  const { emitEventOnTableCreation } = useEmitResourceCreatedEvents();
  const emitEventOnTablesPlanUpgrade = useSetAtom(
    emitEventOnTablesPlanUpgradeSetter,
  );

  const createNewTable = useCallback(() => {
    setShouldCreateNewTable(true);
  }, [setShouldCreateNewTable]);

  // Reset the shouldCreateNewTableAtom to false after the createNewTableResponse completes
  useEffect(() => {
    if (createNewTableResponse !== null) {
      const tableId = createNewTableResponse.id;

      if (tableId) {
        const updatedNodes = updateNode({
          id: nodeId,
          data: {
            tableId,
          },
        });

        saveToBackend({ nodes: updatedNodes });

        refreshTables(); // Trigger tables to be re-fetched, so the new table can be populated in the Tables Typeahead
        refreshTablesUsage(); // Because tableCount would've changed
        setShouldCreateNewTable(false);

        emitEventOnTableCreation(tableId);
      }
    }
  }, [
    refreshTablesUsage,
    createNewTableResponse,
    setShouldCreateNewTable,
    saveToBackend,
    updateNode,
    emitEventOnTableCreation,
    refreshTables,
    nodeId,
  ]);

  const currentPlanLimit = tablesUsage?.maxTableCount ?? 0;
  const planType = getUpgradePlanType({ type: "tables", currentPlanLimit });
  const href = `/app/planbuilder/add-ons?product=tables-${planType}`;

  function UpsellMessage() {
    if (isAdmin) {
      return (
        <Text tag="p" type="SmallPrint1" textAlign="left" color="GrayWarm10">
          You&apos;ve reached your plans Tables limit.&nbsp;
          <Link
            href={href}
            target="_blank"
            onClick={() =>
              emitEventOnTablesPlanUpgrade("Upgrade to create more Tables.")
            }
          >
            Upgrade
          </Link>
          &nbsp;to create more Tables.
        </Text>
      );
    }

    return (
      <Text tag="p" type="SmallPrint1" textAlign="left" color="GrayWarm10">
        You&apos;ve reached your plans Tables limit. Talk to your admin about
        upgrading to create more Tables.
      </Text>
    );
  }

  return (
    <>
      <Button
        color="primary"
        iconBefore="formAdd"
        onClick={createNewTable}
        disabled={tablesUsage?.needsToUpgrade ?? false}
      >
        {CREATE_NEW_TABLE_BUTTON_TEXT}
      </Button>
      {tablesUsage?.needsToUpgrade && <UpsellMessage />}
    </>
  );
}
